import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import KpiCardWithSparkline from './components/KpiCardWithSparkline';
import SvgProgressRing from './components/SvgProgressRing';
import ActivityMiniFeed from './components/ActivityMiniFeed';
import PagesMeasurements from './components/PagesMeasurements';
import AssemblyTotalsWidget from './components/AssemblyTotalsWidget';
import PurchaseOrdersWidget from './components/PurchaseOrdersWidget';
import ModuleCards from './components/ModuleCards';
import * as ROUTES from '../../constants/routes';

const genSparkline = (val, maxVal) => {
  if (!val || !maxVal) return null;
  const base = val / 7;
  const variance = base * 0.4;
  return Array.from({ length: 7 }, (_, i) =>
    Math.max(
      0,
      Math.min(
        maxVal,
        Math.round(base + Math.sin(i * 1.2 + val) * variance),
      ),
    ),
  );
};

const OverviewTab = ({
  displayName,
  selectedProject,
  selectedProjectDate,
  tasks,
  tasksLoading,
  taskStats,
  activityLogs,
  assemblyData,
  assemblyLoading,
  annotatedPages,
  annotatedLoading,
  purchaseOrders,
  poLoading,
  isOrgAdmin,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const sparklineData = useMemo(() => {
    if (!selectedProject || tasks.length === 0) return {};
    const total = tasks.length;
    const maxTask = Math.max(total, 1);
    return {
      total: genSparkline(total, maxTask * 2),
      done: genSparkline(taskStats.done, maxTask),
      inProgress: genSparkline(taskStats.inProgress, maxTask),
      progress: genSparkline(Math.round(taskStats.avgProgress), 100),
    };
  }, [selectedProject, tasks, taskStats]);

  const handleAssemblyTakeoff = () => navigate(ROUTES.REPLAN);

  const dashboardContent = selectedProject ? (
    <>
      <div className="home-project-data-grid">
        <PagesMeasurements
          annotatedPages={annotatedPages}
          annotatedLoading={annotatedLoading}
          assemblyData={assemblyData}
          assemblyLoading={assemblyLoading}
          selectedProject={selectedProject}
          selectedProjectDate={selectedProjectDate}
          isOrgAdmin={isOrgAdmin}
          onTakeoff={handleAssemblyTakeoff}
        />
        <AssemblyTotalsWidget
          assemblyData={assemblyData}
          loading={assemblyLoading}
        />
      </div>
      <div
        className="metrics-row auto-fill"
        style={{ marginBottom: '1.5rem', display : 'none' }} /* not ready for production */
      >
        <div className="glass metric-card">
          <h5>⏱ {t('home.chart_progress_title')}</h5>
          {taskStats.total > 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <SvgProgressRing percent={taskStats.avgProgress} />
              <div className="home-breakdown">
                <div className="home-breakdown-item">
                  <span className="home-bd-dot home-bd-done" />
                  <span className="home-bd-label">
                    {t('home.status_done')}
                  </span>
                  <span className="home-bd-count">
                    {taskStats.done}
                  </span>
                </div>
                <div className="home-breakdown-item">
                  <span className="home-bd-dot home-bd-inprogress" />
                  <span className="home-bd-label">
                    {t('home.status_inprogress')}
                  </span>
                  <span className="home-bd-count">
                    {taskStats.inProgress}
                  </span>
                </div>
                <div className="home-breakdown-item">
                  <span className="home-bd-dot home-bd-notstarted" />
                  <span className="home-bd-label">
                    {t('home.status_notstarted')}
                  </span>
                  <span className="home-bd-count">
                    {taskStats.notStarted}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <p className="home-chart-empty">
              {t('home.chart_no_tasks')}
            </p>
          )}
        </div>

        {activityLogs.length > 0 && (
          <div className="glass metric-card">
            <h5>📡 {t('admin.tab_activity')}</h5>
            <ActivityMiniFeed logs={activityLogs} />
          </div>
        )}
        <PurchaseOrdersWidget
          orders={purchaseOrders}
          loading={poLoading}
          isAdmin={isOrgAdmin}
        />

        <div className="glass metric-card">
          <h5>📊 {t('home.chart_tasks_title')}</h5>
          {taskStats.total > 0 ? (
            <>
              <div
                className="home-stacked-bar"
                style={{ marginBottom: '1rem' }}
              >
                {taskStats.done > 0 && (
                  <div
                    className="home-stack-done"
                    style={{
                      width: `${(taskStats.done / taskStats.total) * 100}%`,
                    }}
                  />
                )}
                {taskStats.inProgress > 0 && (
                  <div
                    className="home-stack-inprogress"
                    style={{
                      width: `${(taskStats.inProgress / taskStats.total) * 100}%`,
                    }}
                  />
                )}
                {taskStats.notStarted > 0 && (
                  <div
                    className="home-stack-notstarted"
                    style={{
                      width: `${(taskStats.notStarted / taskStats.total) * 100}%`,
                    }}
                  />
                )}
              </div>
              <div className="home-breakdown">
                <div className="home-breakdown-item">
                  <span className="home-bd-dot home-bd-done" />
                  <span className="home-bd-label">
                    {t('home.status_done')}
                  </span>
                  <span className="home-bd-count">
                    {taskStats.done} (
                    {Math.round(
                      (taskStats.done / taskStats.total) * 100,
                    )}
                    %)
                  </span>
                </div>
                <div className="home-breakdown-item">
                  <span className="home-bd-dot home-bd-inprogress" />
                  <span className="home-bd-label">
                    {t('home.status_inprogress')}
                  </span>
                  <span className="home-bd-count">
                    {taskStats.inProgress} (
                    {Math.round(
                      (taskStats.inProgress / taskStats.total) * 100,
                    )}
                    %)
                  </span>
                </div>
                <div className="home-breakdown-item">
                  <span className="home-bd-dot home-bd-notstarted" />
                  <span className="home-bd-label">
                    {t('home.status_notstarted')}
                  </span>
                  <span className="home-bd-count">
                    {taskStats.notStarted} (
                    {Math.round(
                      (taskStats.notStarted / taskStats.total) * 100,
                    )}
                    %)
                  </span>
                </div>
              </div>
            </>
          ) : (
            <p className="home-chart-empty">
              {t('home.chart_no_tasks')}
            </p>
          )}
        </div>
      </div>
      <ModuleCards horizontal />
    </>
  ) : (
    <div
      className="glass metric-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <h5>🛠️ {t('home.modules_title')}</h5>
      <p
        style={{
          fontSize: '0.85rem',
          color: 'var(--pico-muted-color)',
          marginBottom: '1.25rem',
        }}
      >
        Select a project to load statistics, or launch any of the
        application tools below.
      </p>
      <div style={{ flex: 1 }}>
        <ModuleCards />
      </div>
    </div>
  );

  return <div className="home-overview-main">{dashboardContent}</div>;
};

export default OverviewTab;
